import type { ReactNode } from 'react'
import type { TimelineElement } from '../../context/TimelineContext'
import { TIMELINE_BOARD_LABELS } from '../../types/schema'
import { Breadcrumb, type CrumbSpec, type DropdownItem } from '../Common'

export interface BoardHeaderProps {
  item: { id: string; title: string }
  dashboardElements: TimelineElement[]
  breadcrumbChain: TimelineElement[]
  jumpToDashboardFromBoard: () => void
  jumpToChainItem: (targetItem: TimelineElement) => void
  children?: ReactNode
}

export function BoardHeader({
  item,
  dashboardElements,
  breadcrumbChain,
  jumpToDashboardFromBoard,
  jumpToChainItem,
  children,
}: BoardHeaderProps) {
  return (
    <div
      className="checklistBoardHeader checklistBoardHeader--flat"
      data-orbit-target="checklist-book"
      data-book-id={item.id}
      data-orbit-title={item.title}
    >
      <div className="checklistBoardHeaderMain checklistBoardHeaderMain--flat">
        {/* Flat toolbar. Mirrors ChecklistsListView's toolbar rhythm:
         * functional canvas breadcrumb on the left. The drawing-board title,
         * description, eyebrow, stats chip, and separate create button were
         * retired here per the flat-canvas spec — creation lives under the
         * existing three-dot canvas tools menu. */}
        <Breadcrumb
          className="checklistBoardBreadcrumb min-w-0 text-xs font-bold text-[var(--muted)]"
          ariaLabel="Checklist canvas breadcrumb"
          crumbs={(() => {
            const trail: CrumbSpec[] = []
            const topGoals = dashboardElements
              .filter((element) => element.type === 'GOAL' && !element.parentId)
              .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
            trail.push({
              key: 'board',
              label: TIMELINE_BOARD_LABELS.DASHBOARD,
              onLabelClick: () => jumpToDashboardFromBoard(),
              ariaLabel: 'board',
              children: topGoals.map<DropdownItem>((goal) => ({
                key: goal.id,
                label: goal.title || 'Untitled',
                onSelect: () => jumpToChainItem(goal),
              })),
            })
            for (const chainItem of breadcrumbChain) {
              const childElements = dashboardElements
                .filter((element) => element.parentId === chainItem.id)
                .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
              trail.push({
                key: chainItem.id,
                label: chainItem.title,
                onLabelClick: () => jumpToChainItem(chainItem),
                ariaLabel: 'chain crumb',
                children: childElements.map<DropdownItem>((child) => ({
                  key: child.id,
                  label: child.title || 'Untitled',
                  onSelect: () => jumpToChainItem(child),
                })),
              })
            }
            return trail
          })()}
        />
      </div>
      <div className="checklistBoardActionsColumn">
        {children}
      </div>
    </div>
  )
}
